import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Store } from '@ngrx/store'
import { HubtypePusherApp } from 'models/hubtype-pusher-app'
import { HubtypeRealtimeCase } from 'models/hubtype-slas'
import { HubtypeCaseTransferEvent } from 'models/hubtype-transfer'
import {
  ICaseAssigned,
  ICaseChatOnline,
  ICaseUpdatedContactReasons,
  IMessageReaction,
  IQueueCountersUpdate,
  PusherEvent,
} from 'models/pusher.model'
import * as PusherTypes from 'pusher-js'
import Pusher from 'pusher-js/with-encryption'
import {
  Observable,
  Subject,
  defer,
  filter,
  finalize,
  first,
  forkJoin,
  iif,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs'
import { isCaseVisibleForUser } from 'utils/inbox-utils'
import { queryStringToObject } from 'utils/string-utils'
import * as auth from '../actions/auth'
import * as deskAction from '../actions/desk'
import {
  ClearPusherSettings,
  FetchPusherSettings,
} from '../actions/infrastructure'
import * as orgAction from '../actions/organization'
import * as UserActions from '../actions/user'
import { SentryTags } from '../constants/sentry'
import { HubtypeCall } from '../models/hubtype-call'
import { HubtypeCase } from '../models/hubtype-case'
import {
  HubtypeMessage,
  HubtypeMessageReaction,
  HubtypeMessageType,
} from '../models/hubtype-message'
import { HubtypeProject } from '../models/hubtype-project'
import { HubtypeProviderAccount } from '../models/hubtype-provider-account'
import {
  HubtypeQueue,
  HubtypeQueueCounter,
  HubtypeQueueUpdated,
} from '../models/hubtype-queue'
import {
  HubtypeUser,
  NotificationsVisibilityMode,
} from '../models/hubtype-user'
import * as fromRoot from '../reducers'
import { CaseList } from '../reducers/desk.state'
import { appOutOfFocus } from '../utils/dom-utils'
import { ConverterService } from './converter.service'
import { DesktopNotifications } from './desktop-notifications'
import { FeedbackService } from './feedback.service'
import { CaseService } from './hubtype-api/case.service'
import {
  HubtypeApiService,
  NO_VERSIONED_API,
} from './hubtype-api/hubtype-api.service'
import { ProjectService } from './hubtype-api/project.service'
import { QueueService } from './hubtype-api/queue.service'
import { UserService } from './hubtype-api/user.service'
import { LoggerService } from './logger.service'
import { WhatsappCallService } from './whatsapp-call.service'

export enum ConnectionStatus {
  LOST = 'lost',
  RECONNECTED = 'reconnected',
}

function pusherVerboseLoggingEnabled() {
  return localStorage.getItem('pusher_verbose_logging')
}

export function pusherVerboseLog(message: string) {
  if (pusherVerboseLoggingEnabled()) {
    console.log(new Date().toISOString(), message)
  }
}

// https://blog.pusher.com/real-time-apps-angular-2/

@Injectable({ providedIn: 'root' })
export class PusherService {
  public organizationId: string
  private channelPrefix: string
  private lastPusherHeartBeatTimestamp: number
  private pusherHeartBeatHandlerInterval
  public me: HubtypeUser
  public pusher: Pusher
  public pusherStatusChannel: PusherTypes.Channel
  public presenceChannel
  public dataChannel
  public dataSLAChannel
  public userChannel
  public pusherConnected = true
  public status: Subject<ConnectionStatus> = new Subject()

  constructor(
    private store: Store<fromRoot.State>,
    private projectService: ProjectService,
    private queueService: QueueService,
    private caseService: CaseService,
    private userService: UserService,
    private desktopNotification: DesktopNotifications,
    private convertService: ConverterService,
    private feedbackService: FeedbackService,
    private apiService: HubtypeApiService,
    private router: Router,
    private whatsappCallService: WhatsappCallService,
    private loggerService: LoggerService
  ) {
    this.disconnectPusherWhenLogout()
    this.connectPusherWhenLoginOrRefresh()
  }

  connectPusherWhenLoginOrRefresh() {
    this.store.select(fromRoot.getMeState).subscribe(me => {
      this.me = me
    })

    this.store
      .select(fromRoot.getPusherApp)
      .pipe(filter(pusherApp => Boolean(pusherApp)))
      .subscribe(pusherApp => {
        this.organizationId = pusherApp.organizationId
        this.channelPrefix = pusherApp.channelPrefix
        let isReconnection = false
        if (this.pusher) {
          pusherVerboseLog('Disconnecting existing pusher connection')
          isReconnection = true
          this.disconnectPusherAndCleanUp()
        }
        this.connect(pusherApp)
        if (isReconnection) {
          this.status.next(ConnectionStatus.RECONNECTED)
        }
      })
  }

  disconnectPusherWhenLogout() {
    this.store
      .select(fromRoot.isLoggedIn)
      .pipe(filter(isLogged => !isLogged))
      .subscribe(() => {
        this.disconnectPusherAndCleanUp()
      })
  }

  private disconnectPusherAndCleanUp() {
    this.pusher?.disconnect()
    if (this.pusherHeartBeatHandlerInterval) {
      clearInterval(this.pusherHeartBeatHandlerInterval)
    }
    this.pusher = null
    this.store.dispatch(new ClearPusherSettings())
  }

  connectSLAs() {
    this.dataSLAChannel = this.subscribeToPusherChannel(
      this.dataSLAChannelName()
    )
    this.dataSLAChannel?.bind('new_sla_item', data => {
      this.store.dispatch(
        new deskAction.AddDashboardCaseAction(
          this.convertService.jsonConvert.deserializeObject(
            data.message,
            HubtypeRealtimeCase
          )
        )
      )
    })

    this.dataSLAChannel?.bind('queue_counters_update', data =>
      this.handleQueueCountersUpdate(data)
    )
  }

  unsubscribeFromSLAChannels() {
    if (this.pusher) {
      this.pusher.unsubscribe(this.dataSLAChannelName())
    }
  }

  presenceChannelName() {
    return `presence-${this.channelPrefix}_organization_${this.organizationId}`
  }

  dataChannelName() {
    return `private-${this.channelPrefix}_organization_${this.organizationId}_v2`
  }

  dataSLAChannelName() {
    return `private-${this.channelPrefix}_organization_${this.organizationId}_slas`
  }

  getUserChannelName() {
    return `private-${this.channelPrefix}_hubtypeuser_${this.me.id}`
  }

  private initializePusher(pusherApp: HubtypePusherApp) {
    if (pusherVerboseLoggingEnabled()) {
      // show pusher logs on console (very verbose)
      Pusher.log = message => {
        console.log(new Date().toISOString(), message)
      }
    }
    console.log(
      new Date().toISOString(),
      'Initializing Pusher with new settings:',
      pusherApp
    )
    const PING_PONG_INTERVAL_MILISECONDS = 1 * 1000 // Every x miliseconds pusher verifies if the connection still alive.
    const PONG_TIMEOUT_MILISECONDS = 30 * 1000 // The max timeout waiting for the pong response from the server to identify the connection as broken.
    const config: PusherTypes.Options = {
      cluster: pusherApp.cluster,
      // @ts-ignore
      authTransport: 'customPusherAuthorizer',
      activityTimeout: PING_PONG_INTERVAL_MILISECONDS,
      pongTimeout: PONG_TIMEOUT_MILISECONDS,
    }

    this.setCustomPusherAuthorizer()
    this.pusher = new Pusher(pusherApp.key, config)
  }

  private setCustomPusherAuthorizer() {
    // To be able to handle correctly the auth token of the pusher authorization request with the angular interceptor we decided to use a custom pusher Authorizer.
    // https://support.pusher.com/hc/en-us/articles/4412502563473-Providing-A-Custom-Channels-Authoriser
    const supportedAuthorizers = Pusher.Runtime.getAuthorizers()
    supportedAuthorizers.customPusherAuthorizer = (
      context,
      params,
      options,
      authorizationType,
      callback
    ) => {
      this.authorizeChannel(queryStringToObject(params)).subscribe({
        next: authorization => callback(false, authorization),
        error: error => callback(true, error),
      })
    }
    Pusher.Runtime.getAuthorizers = () => supportedAuthorizers
  }

  private authorizeChannel(params: object) {
    return this.apiService
      .post('/pusher/auth', params, null, NO_VERSIONED_API)
      .pipe(first())
  }

  private logErrorInSentry(
    errorName: string,
    errorMessage: string,
    extra: object
  ) {
    this.loggerService.logRegularError(errorName, errorMessage, {
      tags: { [SentryTags.PUSHER_ERROR]: true },
      extra,
    })
  }

  private subscribeToPusherChannel(
    channelName: string,
    succeeded_callback = undefined,
    error_callback = undefined
  ): PusherTypes.Channel {
    if (!this.pusher) {
      console.log(
        new Date().toISOString(),
        `Trying to subscribe to channel ${channelName} but pusher is not initialized`
      )
      return
    }

    const channel = this.pusher.subscribe(channelName)
    if (succeeded_callback) {
      pusherVerboseLog(`Setting succeeded callback for channel ${channelName}`)
      channel.bind('pusher:subscription_succeeded', succeeded_callback)
    }
    if (error_callback) {
      pusherVerboseLog(`Setting error callback for channel ${channelName}`)
      channel.bind('pusher:subscription_error', error_callback)
    } else {
      pusherVerboseLog(
        `Setting default error callback for channel ${channelName}`
      )
      channel.bind('pusher:subscription_error', data => {
        console.log(
          new Date().toISOString(),
          `Error subscribing to channel ${channelName}`,
          data
        )
        this.raiseLostConnectionStatus()
      })
    }
    return channel
  }

  private raiseLostConnectionStatus() {
    this.pusherConnected = false
    this.store.dispatch(new FetchPusherSettings())
    this.status.next(ConnectionStatus.LOST)
  }

  public pusherStatusManager = states => {
    let connectionStatus: ConnectionStatus = null

    if (states.previous === 'connected' && states.current === 'connecting') {
      this.pusherConnected = false
      connectionStatus = ConnectionStatus.LOST
      this.store.dispatch(new FetchPusherSettings())
    }

    if (
      !this.pusherConnected &&
      states.previous === 'connecting' &&
      states.current === 'connected'
    ) {
      this.pusherConnected = true
      connectionStatus = ConnectionStatus.RECONNECTED
    }

    if (connectionStatus) {
      this.status.next(connectionStatus)
    }
  }

  private setUpPusherStatusHandler(pusherApp: HubtypePusherApp) {
    this.pusherStatusChannel = this.subscribeToPusherChannel(
      pusherApp.statusChannel
    )

    const msUntilConsiderExpiredHeartBeat =
      (pusherApp.heartBeatPeriodInSeconds + 30) * 1000
    this.lastPusherHeartBeatTimestamp = Date.now()
    this.pusherHeartBeatHandlerInterval = setInterval(() => {
      const msSinceLastHeartBeat =
        Date.now() - this.lastPusherHeartBeatTimestamp
      pusherVerboseLog(
        `Checking pusher heart beat: msSinceLastHeartBeat=${msSinceLastHeartBeat} > ${msUntilConsiderExpiredHeartBeat}`
      )
      if (msSinceLastHeartBeat > msUntilConsiderExpiredHeartBeat) {
        console.log(
          new Date().toISOString(),
          `Pusher heart beat expired after ${msSinceLastHeartBeat} ms. Fetching pusher settings`
        )
        this.store.dispatch(new FetchPusherSettings())
      }
    }, 30 * 1000)

    this.pusherStatusChannel.bind('connection_heart_beat', data => {
      // We should be receiving this every 3 minutes
      this.lastPusherHeartBeatTimestamp = Date.now()
    })

    this.pusherStatusChannel.bind('app_deactivated', data => {
      this.store.dispatch(new FetchPusherSettings())
    })
  }

  private connect(pusherApp: HubtypePusherApp) {
    this.initializePusher(pusherApp)

    this.pusher.connection.bind('state_change', this.pusherStatusManager)

    this.setUpPusherStatusHandler(pusherApp)

    const presenceChannelName = this.presenceChannelName()
    const dataChannelName = this.dataChannelName()
    const userChannelName = this.getUserChannelName()
    const requiredChannels = [
      presenceChannelName,
      dataChannelName,
      userChannelName,
    ]
    const subscribedChannels = new Set<string>()
    const onRequiredChannelSubscribed = (channelName: string) => () => {
      subscribedChannels.add(channelName)
      if (requiredChannels.every(name => subscribedChannels.has(name))) {
        pusherVerboseLog(
          'Successfully subscribed to all required channels. Setting up pusher connection status action'
        )
        this.store.dispatch(new auth.SetPusherConnectionStatusAction(true))
      }
    }

    this.presenceChannel = this.subscribeToPusherChannel(
      presenceChannelName,
      onRequiredChannelSubscribed(presenceChannelName)
    )
    this.dataChannel = this.subscribeToPusherChannel(
      dataChannelName,
      onRequiredChannelSubscribed(dataChannelName)
    )
    this.userChannel = this.subscribeToPusherChannel(
      userChannelName,
      onRequiredChannelSubscribed(userChannelName)
    )

    // PUSHER EVENTS ----------------------------------------------------------------------------------------
    // Start User Channel Events ----------------------------------------------------------------------------
    this.userChannel.bind('start_typing', data => {
      this.handleEnduserTypingEvent(data?.message?.case_id, true)
    })
    this.userChannel.bind('end_typing', data => {
      this.handleEnduserTypingEvent(data?.message?.case_id, false)
    })
    this.userChannel.bind('new_message', this.handleNewMessage)
    this.userChannel.bind('message_deleted', this.handleMessageDeleted)
    this.userChannel.bind('message_reaction', this.handleMessageReaction)
    this.userChannel.bind(
      'message_ack_received',
      this.messageAckReceivedManager
    )
    this.userChannel.bind('case_chat_online', this.handleChatOnlineChanged)
    // End User Channel Events ------------------------------------------------------------------------------

    // Start Data Channel Events ----------------------------------------------------------------------------
    this.dataChannel.bind('case_chat_online', this.handleChatOnlineChanged)
    this.dataChannel.bind('user_activated', this.handleUserCreated)
    this.dataChannel.bind('user_created', this.handleUserCreated)
    this.dataChannel.bind('user_deactivated', this.handleUserDeactivated)
    this.dataChannel.bind('queue_updated', this.updateQueue)
    this.dataChannel.bind('case_created', this.handleCaseCreated)
    this.dataChannel.bind('case_assigned_v2', this.handleCaseAssigned)
    this.dataChannel.bind('case_changed_status', this.handleCaseChangedStatus)
    this.dataChannel.bind('case_transferred', this.handleCaseTransferred)
    this.dataChannel.bind('new_message', this.handleNewMessage)
    this.dataChannel.bind('message_reaction', this.handleMessageReaction)
    this.dataChannel.bind(
      'case_contact_reasons_changed',
      this.handleContactReasonsChanged
    )
    // Case closed by the enduser at this moment just raised when apple user deletes the conversation
    this.dataChannel.bind('case_closed', data => {
      this.caseService.closeTicketByEnduser(data?.message?.case_id)
    })

    this.dataChannel.bind('project_created', data => {
      if (this.me.is_admin) {
        const p = this.convertService.jsonConvert.deserializeObject(
          data.message,
          HubtypeProject
        )
        this.store.dispatch(new orgAction.AddProjectAction(p))
      }
    })

    this.dataChannel.bind('project_deleted', data => {
      const p = this.convertService.jsonConvert.deserializeObject(
        data.message,
        HubtypeProject
      )
      this.store.dispatch(new orgAction.DeleteProjectAction(p))
    })

    this.dataChannel.bind('project_updated', data => {
      const p = this.convertService.jsonConvert.deserializeObject(
        data.message,
        HubtypeProject
      )
      this.store
        .select(fromRoot.getProjects)
        .pipe(
          first(),
          filter(projects => projects.some(project => project.id === p.id))
        )
        .subscribe(projects => {
          this.store.dispatch(new orgAction.UpdateProjectAction(p))
        })
    })

    this.dataChannel.bind('queue_created', data => {
      const q = this.convertService.jsonConvert.deserializeObject(
        data.message,
        HubtypeQueue
      )
      this.store.dispatch(new orgAction.AddQueueAction(q))
    })

    this.dataChannel.bind('queue_deleted', data => {
      const q = this.convertService.jsonConvert.deserializeObject(
        data.message,
        HubtypeQueue
      )
      this.store.dispatch(new orgAction.DeleteQueueAction(q))
    })

    this.dataChannel.bind('manager_added', data => {
      const projectId = data.message.project.id
      const userId = data.message.manager_id
      this.addManager(projectId, userId)
    })

    this.dataChannel.bind('manager_deleted', data => {
      const projectId = data.message.project.id
      const userId = data.message.manager_id
      this.deleteManager(projectId, userId)
    })

    this.dataChannel.bind('agent_assigned', data => {
      const queueId = data?.message?.queue_id
      const agentId = data?.message?.agent_id
      if (!queueId) {
        this.logErrorInSentry(
          'Pusher: Unable to assign new agent to the queue',
          'Unable to assign new agent to the queue, the queue ID is missing.',
          { data }
        )
        return
      }
      if (!agentId) {
        this.logErrorInSentry(
          'Pusher: Unable to assign new agent to the queue',
          'Unable to assign new agent to the queue, the agent ID is missing.',
          { data }
        )
        return
      }
      this.agentAssigned(queueId, agentId)
    })

    this.dataChannel.bind('agent_unassigned', data => {
      const queueId = data?.message?.queue_id
      const agentId = data?.message?.agent_id
      if (!queueId) {
        this.logErrorInSentry(
          'Pusher: Unable to assign new agent to the queue',
          'Unable to assign new agent to the queue, the queue ID is missing.',
          { data }
        )
        return
      }
      if (!agentId) {
        this.logErrorInSentry(
          'Pusher: Unable to assign new agent to the queue',
          'Unable to assign new agent to the queue, the agent ID is missing.',
          { data }
        )
        return
      }

      this.agentUnassigned(queueId, agentId)
    })

    this.dataChannel.bind('agent_changed_status', data => {
      this.store.dispatch(
        UserActions.changeUserStatus({
          userId: data.message.user.id,
          status: data.message.next_status,
        })
      )
    })

    this.dataChannel.bind('provider_account_created', data => {
      const providerAccount = this.convertService.jsonConvert.deserializeObject(
        data.message,
        HubtypeProviderAccount
      )
      this.store.dispatch(
        new orgAction.AddProviderAccountAction(providerAccount)
      )
    })

    this.dataChannel.bind('provider_account_updated', data => {
      const providerAccount = this.convertService.jsonConvert.deserializeObject(
        data.message,
        HubtypeProviderAccount
      )
      this.store.dispatch(
        new orgAction.UpdateProviderAccountAction(providerAccount)
      )
    })

    this.dataChannel.bind('provider_account_deleted', data => {
      const providerAccount = this.convertService.jsonConvert.deserializeObject(
        data.message,
        HubtypeProviderAccount
      )
      this.store.dispatch(
        new orgAction.DeleteProviderAccountAction(providerAccount)
      )
    })

    this.dataChannel.bind('call_connect_answer', this.handleCallConnectAnswer)
    this.dataChannel.bind('call_status_changed', this.handleCallStatusChanged)

    // End Data Channel Events ------------------------------------------------------------------------------
  }

  private handleCallConnectAnswer = (callEvent: PusherEvent<HubtypeCall>) => {
    const call = this.convertService.jsonConvert.deserializeObject(
      callEvent.message,
      HubtypeCall
    )
    this.whatsappCallService.handleCallConnectAnswer(call).subscribe({
      error: err =>
        console.error('Could not handle WhatsApp call answer:', err),
    })
  }

  private handleCallStatusChanged = (callEvent: PusherEvent<HubtypeCall>) => {
    const call = this.convertService.jsonConvert.deserializeObject(
      callEvent.message,
      HubtypeCall
    )
    this.whatsappCallService.handleCallStatusChanged(call)
  }

  private handleCaseCreated = (caseEvent: PusherEvent<HubtypeCase>) => {
    const caze = this.convertService.jsonConvert.deserializeObject(
      caseEvent.message,
      HubtypeCase
    )
    if (!isCaseVisibleForUser(this.me, caze)) {
      return
    }

    this.store.dispatch(
      new deskAction.UpdateCaseListsIfNeededAction({ me: this.me, case: caze })
    )

    this.notifyNewCaseDesktopNotificationIfNeeded(caze)
  }

  private notifyNewCaseDesktopNotificationIfNeeded(caze: HubtypeCase) {
    if (
      this.shouldNotifyDependingOnTabFocus() &&
      this.me.hasNewCaseNotificationsEnabled()
    ) {
      let message = 'New case received'
      this.desktopNotification.notify(
        '',
        message,
        this.me.notifications_duration,
        caze.id
      )
    }
  }
  private notifyNewAssignedCaseDesktopNotificationIfNeeded(
    caze: HubtypeCase,
    isAutoAssign: boolean
  ) {
    if (
      isAutoAssign &&
      caze.isAssignedTo(this.me.id) &&
      this.shouldNotifyDependingOnTabFocus() &&
      this.me.notifications_assigned_case
    ) {
      this.desktopNotification.notify(
        '',
        'New case assigned',
        this.me.notifications_duration,
        caze.id
      )
    }
  }

  private handleCaseAssigned = (caseEvent: PusherEvent<ICaseAssigned>) => {
    const caze = this.convertService.jsonConvert.deserializeObject(
      caseEvent.message.case,
      HubtypeCase
    )
    const isAutoAssign = caseEvent.message.is_auto_assign

    this.store.dispatch(
      new deskAction.UpdateCaseListsIfNeededAction({ me: this.me, case: caze })
    )
    this.notifyNewAssignedCaseDesktopNotificationIfNeeded(caze, isAutoAssign)
  }

  private handleCaseChangedStatus = (caseEvent: PusherEvent<HubtypeCase>) => {
    const caze = this.convertService.jsonConvert.deserializeObject(
      caseEvent.message,
      HubtypeCase
    )
    this.store.dispatch(
      new deskAction.UpdateCaseListsIfNeededAction({ me: this.me, case: caze })
    )
  }

  private handleCaseTransferred = (
    caseEvent: PusherEvent<HubtypeCaseTransferEvent>
  ) => {
    const caseTransferEvent = this.convertService.jsonConvert.deserializeObject(
      caseEvent.message,
      HubtypeCaseTransferEvent
    )

    this.handleTransferNotifications(caseTransferEvent)

    this.store.dispatch(
      new deskAction.UpdateCaseListsIfNeededAction({
        me: this.me,
        case: caseTransferEvent.case,
      })
    )
  }

  handleTransferNotifications(caseTransferEvent: HubtypeCaseTransferEvent) {
    if (this.me.id === caseTransferEvent.user_executor_id) {
      this.feedbackService.success('The case was transferred correctly')
    } else if (this.me.id === caseTransferEvent.previous_assigned_user_id) {
      if (
        caseTransferEvent.previous_assigned_user_id ===
        caseTransferEvent.case.assigned_to?.id
      ) {
        this.feedbackService.info(
          `${caseTransferEvent.user_executor_name} has moved one of your cases to ${caseTransferEvent?.current_project_name} | ${caseTransferEvent?.current_queue_name}`
        )
        this.showTransferDesktopNotification(
          'One of your cases has been transferred',
          caseTransferEvent.case.id
        )
      } else {
        this.feedbackService.info(
          `${caseTransferEvent.user_executor_name} has transferred one of your cases`
        )
        this.showTransferDesktopNotification(
          'One of your cases has been transferred',
          null
        )
      }
    } else if (this.me.id === caseTransferEvent.case.assigned_to?.id) {
      this.feedbackService.info(
        `${caseTransferEvent.user_executor_name} has transferred one case to you`
      )
      this.showTransferDesktopNotification(
        'A case has been transferred to you',
        caseTransferEvent.case.id
      )
    } else if (
      !this.me.hasQueuePermission(caseTransferEvent.previous_queue_id) &&
      this.me.hasQueuePermission(caseTransferEvent.case.queue_id) &&
      !caseTransferEvent.case.assigned_to?.id
    ) {
      this.showTransferDesktopNotification(
        'You have a new transferred case',
        caseTransferEvent.case.id
      )
    }
  }

  showTransferDesktopNotification(message: string, caseId: string) {
    if (
      this.shouldNotifyDependingOnTabFocus() &&
      this.me.notifications_new_case
    ) {
      this.desktopNotification.notify(
        message,
        'Case Transferred',
        this.me.notifications_duration,
        caseId
      )
    }
  }

  handleUserCreated = data => {
    const userId = data.message.user_id

    this.userService.fetchUser(userId).subscribe(user => {
      this.store.dispatch(UserActions.addUser({ user }))
    })
  }

  handleUserDeactivated = data => {
    const userId = data.message.user_id

    this.store.dispatch(UserActions.deleteUser({ userId }))
    if (userId == this.me.id) {
      this.logoutCurrentUser()
    }
  }

  private logoutCurrentUser() {
    this.userService
      .setOffline()
      .pipe(
        first(),
        finalize(() => {
          this.store.dispatch(new auth.LogoutAction())
          this.router.navigate(['/sign-in'])
        })
      )
      .subscribe()
  }

  handleChatOnlineChanged = (data: PusherEvent<ICaseChatOnline>) => {
    this.store.dispatch(
      new deskAction.UpdateCaseChatOnline({
        caseId: data?.message?.case_id,
        chatId: data?.message?.chat_id,
        isOnline: data?.message?.is_online,
      })
    )
  }

  handleContactReasonsChanged = (
    data: PusherEvent<ICaseUpdatedContactReasons>
  ) => {
    if (!this.me.hasTestCasesVisibility(data.message.is_test)) {
      return
    }
    const { case_id, new_contact_reasons } = data.message
    forkJoin([
      this.store.select(fromRoot.getCaseFromInbox(case_id)).pipe(first()),
      this.store.select(fromRoot.getArchiveCase(case_id)).pipe(first()),
    ]).subscribe({
      next: ([inboxCase, archiveCase]) => {
        if (inboxCase) {
          this.store.dispatch(
            new deskAction.UpdateInboxCaseContactReasons({
              case_id,
              contact_reasons: new_contact_reasons,
            })
          )
        }
        if (archiveCase) {
          this.store.dispatch(
            new deskAction.UpdateArchiveCaseContactReasons({
              case_id,
              contact_reasons: new_contact_reasons,
            })
          )
        }
      },
    })
  }

  handleQueueCountersUpdate = (data: PusherEvent<IQueueCountersUpdate>) => {
    if (!this.me.hasQueuePermission(data.message.id)) {
      return
    }
    const queueCounters = this.convertService.jsonConvert.deserializeObject(
      data.message,
      HubtypeQueueCounter
    )
    this.store.dispatch(new orgAction.UpdateQueueCounters(queueCounters))
  }

  handleNewMessage = (data: PusherEvent<HubtypeMessage>) => {
    if (
      !this.me.hasQueuePermission(data.message.queue_id) ||
      !this.me.hasTestCasesVisibility(data.message.is_test)
    ) {
      return
    }

    const m = this.convertService.jsonConvert.deserializeObject(
      data.message,
      HubtypeMessage
    )
    this.store
      .select(fromRoot.getCaseFromInbox(m.case_id))
      .pipe(take(1), filter(Boolean))
      .subscribe((storedCase: HubtypeCase) => {
        if (
          this.shouldNotifyDependingOnTabFocus() &&
          this.shouldNotifyNewMessage(storedCase, m)
        ) {
          this.desktopNotification.notify(
            m.text,
            storedCase?.chat?.name + ' :',
            this.me.notifications_duration,
            m.case_id
          )
        }
        this.store.dispatch(
          new deskAction.NewMessageReceivedAction({
            case: storedCase,
            message: m,
          })
        )
      })
  }

  handleMessageDeleted = data => {
    const message = this.convertService.jsonConvert.deserializeObject(
      data.message,
      HubtypeMessage
    )
    this.store.dispatch(new deskAction.MessageDeletedReceivedAction(message))
  }

  handleMessageReaction = (data: PusherEvent<IMessageReaction>) => {
    const { id, case_id, action, created_at, reacted_to, is_enduser, emoji } =
      data.message ?? {}
    if (!reacted_to?.id || !reacted_to?.reactions) {
      return
    }
    this.store.dispatch(
      new deskAction.MessageReactionsUpdatedAction({
        messageId: reacted_to.id,
        caseId: case_id,
        reactions: this.convertService.jsonConvert.deserializeArray(
          reacted_to.reactions,
          HubtypeMessageReaction
        ),
      })
    )

    // The persisted reaction row turns the reaction into a timeline entry
    // for the case being viewed. Agent reactions (is_enduser false) render
    // only as chips, so they no-op here.
    if (is_enduser && id && created_at) {
      const reactionMessage = new HubtypeMessage()
      reactionMessage.id = id
      reactionMessage.type = HubtypeMessageType.REACTION
      reactionMessage.action = action
      reactionMessage.created_at = created_at
      reactionMessage.case_id = case_id
      // The emoji is stored on `text` to match the backend's canonical shape
      // for reaction rows (the REST history endpoint persists it there), so a
      // reaction loaded live and one loaded from history render identically.
      reactionMessage.text = emoji
      reactionMessage.extra_data = {
        reacted_to: { id: reacted_to.id },
      }
      this.store.dispatch(
        new deskAction.ReactionMessageReceivedAction({
          message: reactionMessage,
        })
      )
    }
  }

  messageAckReceivedManager = data => {
    const message = this.convertService.jsonConvert.deserializeObject(
      data.message,
      HubtypeMessage
    )
    this.store.dispatch(new deskAction.NewACKMessageReceivedAction(message))
  }

  shouldNotifyNewMessage(c: HubtypeCase, m: HubtypeMessage): boolean {
    return (
      Boolean(m?.is_enduser) &&
      c.isAssignedTo(this.me.id) &&
      this.me.notifications_new_message
    )
  }

  updateQueue = (data: PusherEvent<HubtypeQueueUpdated>) => {
    const updatedQueueData: HubtypeQueueUpdated = data.message
    if (!updatedQueueData.id) {
      this.logErrorInSentry(
        'Pusher: Unable to update the queue',
        'Unable to update the queue, the queue ID is missing',
        { data }
      )
      return
    }

    if (!this.me.hasQueuePermission(updatedQueueData.id)) {
      return
    }
    this.store
      .select(fromRoot.getQueueById(updatedQueueData.id))
      .pipe(first())
      .subscribe(queue => {
        if (!queue) {
          return
        }
        queue.setUpdatedFields(updatedQueueData)
        this.store.dispatch(new orgAction.UpdateQueueAction(queue))
      })
  }

  handleEnduserTypingEvent(caseId: string, isTyping: boolean) {
    this.caseService
      .getStoredCase(caseId)
      .pipe(first())
      .subscribe((storedCase: HubtypeCase) => {
        if (storedCase?.id === caseId) {
          this.caseService.updateEndUserTyping(caseId, isTyping)
        }
      })
  }

  //1. If I don't have this queue in my store and I'm not this agent, return null. Not need anything to update
  //2. If I don't have this queue in my store and I'm this agent, find and download the queue from backend .Jump to step 4
  //3. If I have this queue in my store and I'm not this agent, refresh queue's agent
  //4. If I have this queue in my store and I'm this agent, download project if needed and load cases of the queue
  agentAssigned(queueId: string, agentId: string) {
    this.store
      .select(fromRoot.getUser(agentId))
      .pipe(take(1))
      .subscribe(storeUser => {
        if (!storeUser) {
          this.logErrorInSentry(
            'Pusher: Unable to assign new agent to the queue',
            'Unable to assign new agent to the queue, the agent ID not found',
            { queueId, agentId }
          )
          return
        }
        //Queue synchronized
        this.getAndSyncQueue(queueId, this.me.id === agentId)
          .pipe(filter(queue => Boolean(queue)))
          .subscribe(queue => {
            //Update agents
            this.store.dispatch(
              new orgAction.AddAgentToQueueAction({
                userId: agentId,
                queue: this.convertService.jsonConvert.deserializeObject(
                  queue,
                  HubtypeQueue
                ),
              })
            )

            if (this.me.id === agentId) {
              //Project sync
              this.getAndSyncProject(queue.project_id, true).subscribe()

              //Load cases
              this.loadAllCases()
            }
          })
      })
  }

  agentUnassigned(queueId: string, agentId: string) {
    this.store
      .select(fromRoot.getUser(agentId))
      .pipe(take(1))
      .subscribe(u => {
        if (!u) {
          this.logErrorInSentry(
            'Pusher: Unable to unassign agent from the queue',
            'Unable to unassign agent from the queue, the agent ID not found',
            { queueId, agentId }
          )
          return
        }
        this.store
          .select(fromRoot.getQueueByIdFromOrg(queueId))
          .pipe(
            take(1),
            filter(queue => Boolean(queue))
          )
          .subscribe(q => {
            const queue = this.convertService.jsonConvert.deserializeObject(
              q,
              HubtypeQueue
            )
            this.store.dispatch(
              new orgAction.DeleteAgentFromQueueAction({
                userId: agentId,
                queue,
              })
            )
            if (agentId === this.me.id) {
              //Update queues
              this.store.dispatch(new orgAction.DeleteQueueAction(queue))
              //Remove waiting cases
              this.store.dispatch(
                new deskAction.DeleteCasesByQueueAction({
                  queueId: q.id,
                })
              )
            }
          })
      })
  }

  //1. If I don't have this project in my store and I'm not the userId, ignore.
  //2. If I don't have this project in my store and I'm the userId, find and download the project from backend .Jump to step 4
  //3. If I have this project in my store and I'm not the userId, dispach addManager in store
  //4. If I have this project in my store and I'm the userId, add queues to store and download cases from backend
  addManager(projectId: string, userId: string) {
    this.store
      .select(fromRoot.getUser(userId))
      .pipe(
        take(1),
        filter(user => Boolean(user))
      )
      .subscribe(u => {
        this.getAndSyncProject(projectId, userId === this.me.id)
          .pipe(filter(project => Boolean(project)))
          .subscribe(project => {
            this.store.dispatch(
              new orgAction.AddManagerToProjectAction({
                userId: userId,
                project: this.convertService.jsonConvert.deserializeObject(
                  project,
                  HubtypeProject
                ),
              })
            )
            if (this.me.id === userId) {
              //Update queues
              project.queues?.forEach(queue => {
                this.store.dispatch(new orgAction.AddQueueAction(queue))
              })

              this.loadAllCases()
            }
          })
      })
  }

  deleteManager(projectId: string, userId: string) {
    this.store
      .select(fromRoot.getUser(userId))
      .pipe(
        take(1),
        filter(user => Boolean(user))
      )
      .subscribe(u => {
        this.store
          .select(fromRoot.getProject(projectId))
          .pipe(
            take(1),
            filter(project => Boolean(project))
          )
          .subscribe(project => {
            this.store.dispatch(
              new orgAction.DeleteManagerFromProjectAction({
                userId: userId,
                project: this.convertService.jsonConvert.deserializeObject(
                  project,
                  HubtypeProject
                ),
              })
            )
            if (userId === this.me.id) {
              //Update queues
              project.queues?.forEach(queue => {
                this.store.dispatch(new orgAction.DeleteQueueAction(queue))
                this.store.dispatch(
                  new deskAction.DeleteCasesByQueueAction({
                    queueId: queue.id,
                  })
                )
              })
              this.store.dispatch(new orgAction.DeleteProjectAction(project))
            }
          })
      })
  }

  getAndSyncProject(projectId: string, forceSync: boolean) {
    return this.store.select(fromRoot.getProject(projectId)).pipe(
      take(1),
      switchMap(project =>
        iif(
          () => Boolean(project),
          of(project),
          iif(
            // why use defer => https://stackoverflow.com/questions/54097971/rxjs-iif-arguments-are-called-when-shouldnt
            () => forceSync === true,
            defer(() =>
              this.projectService.get(projectId, true).pipe(
                take(1),
                tap(projectFromBackend => {
                  this.store.dispatch(
                    new orgAction.AddProjectAction(projectFromBackend)
                  )
                })
              )
            ),
            defer(() => of(null))
          )
        )
      )
    )
  }

  getAndSyncQueue(queueId, forceSync): Observable<HubtypeQueue> {
    return this.store.select(fromRoot.getQueueByIdFromOrg(queueId)).pipe(
      take(1),
      switchMap(queue =>
        iif(
          () => Boolean(queue),
          of(queue),
          iif(
            // why use defer => https://stackoverflow.com/questions/54097971/rxjs-iif-arguments-are-called-when-shouldnt
            () => forceSync === true,
            defer(() =>
              this.queueService.get(queueId).pipe(
                take(1),
                tap(queueService => {
                  this.store.dispatch(
                    new orgAction.AddQueueAction(queueService)
                  )
                })
              )
            ),
            defer(() => of(null))
          )
        )
      )
    )
  }

  loadAllCases() {
    this.caseService.loadCasesInParallel(CaseList.ATTENDING)
    this.caseService.loadCasesInParallel(CaseList.IDLE)
    this.caseService.loadCasesInParallel(CaseList.WAITING)
  }

  private shouldNotifyDependingOnTabFocus() {
    const alwaysNotify =
      this.me.notifications_visibility_mode ===
      NotificationsVisibilityMode.ALWAYS
    const whenUnfocused =
      this.me.notifications_visibility_mode ===
      NotificationsVisibilityMode.WHEN_UNFOCUSED
    const isTabUnfocused = appOutOfFocus()

    const isNotificationEnabled =
      alwaysNotify || (whenUnfocused && isTabUnfocused)
    const isNotificationDurationEnabled = this.me.notifications_duration > 0

    return isNotificationEnabled && isNotificationDurationEnabled
  }
}
